// buildErrorCorrectionPayload.ts
// Utility to merge user corrections into the original API object for error correction submit

/**
 * Merges user-entered corrections into the original API object.
 * @param originalApiObject The original API object with all keys/values
 * @param formValues The current form values (user corrections)
 * @param fieldMapping (optional) Map of form field names to API keys (if needed for mapping)
 * @returns The merged payload object for error correction submit
 */
export function buildErrorCorrectionPayload(
  originalApiObject: Record<string, any>,
  formValues: Record<string, any>,
  fieldMapping?: Record<string, string>
): Record<string, any> {
  // If a mapping is provided, use it to map form fields to API keys
  let corrections: Record<string, any> = {};
  if (fieldMapping) {
    Object.entries(formValues).forEach(([formField, value]) => {
      const apiKey = fieldMapping[formField] || formField;
      corrections[apiKey] = value;
    });
  } else {
    corrections = { ...formValues };
  }
  

  const errorFieldMap: Record<string, string> = {
    estimatedTuitionFee: 'actualTutionFee',
    estimatedBooksFee: 'actualBooksFee',
    estimatedLabFee: 'actualLabFee',
    estimatedRegistrationFee: 'actualRegistFee',
    estimatedOtherFee: 'actualOtherFee',
    estimatedAirfareBaggageFee: 'actualAirfareBaggageFee',
    estimatedParkTollFee: 'actualParkTollFee',
    estimatedAutoRentalFee: 'actualAutoRentalFee',
    estimatedPublicTransportFee: 'actualPublicTransportFee',
    estimatedHotelFee: 'actualHotelFee',
    estimatedMilesDollarValue: 'actualMilesDollarValue',
    applicationTerm: 'applTerm',
    applicationYear: 'applYear',
    courseDeliveryType: 'courseDelyType',
    degreeCompletionYear: 'degreeComplYr',
    programField: 'progField',
    programType: 'progType',
    schoolName: 'istSchoolName',
    courseProgramType: 'programType',
    classSchoolName: 'schoolName',
  };
  let merged = { ...originalApiObject, ...corrections };
  Object.entries(errorFieldMap).forEach(([estimatedKey, apiKey]) => {
    if (corrections[estimatedKey] !== undefined && corrections[estimatedKey] !== null && corrections[estimatedKey] !== "") {
      merged[apiKey] = corrections[estimatedKey];
      // Remove the estimated* field from the merged payload
      if (merged.hasOwnProperty(estimatedKey)) {
        delete merged[estimatedKey];
      }
    }
  });

  // Special handling: if degreeName is present in corrections, set both progDegreeName and courseDegreeName, but do not send degreeName itself
  if (corrections.degreeName !== undefined && corrections.degreeName !== null && corrections.degreeName !== "") {
    merged["progDegreeName"] = corrections.degreeName;
    merged["courseDegreeName"] = corrections.degreeName;
    if (merged.hasOwnProperty("degreeName")) {
      delete merged["degreeName"];
    }
  }
 

  // Always stringify errors as required by the API
  // If errors is an array, try to preserve the original backend keys if present
  if (Array.isArray(merged.errors)) {
    // If the array contains objects with 'backendKey', use that as the key
    const errorObj: Record<string, string[]> = {};
    merged.errors.forEach((err: any) => {
      // Prefer backendKey if present, else fallback to fieldName
      const key = err?.backendKey || err?.fieldName;
      if (key && err?.errorMessage) {
        if (!errorObj[key]) errorObj[key] = [];
        errorObj[key].push(err.errorMessage);
      }
    });
    merged.errors = errorObj;
  }
  if (merged.errors && typeof merged.errors === 'object' && merged.errors !== null) {
    merged.errors = JSON.stringify(merged.errors);
  }


  return merged;
}
